import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { THROUGHPUT_CAPACITY_KEY } from '../production-planner/capacity-view/queries';
import { PRODUCTION_PLANNER_KEY } from '../production-planner/queries';
import { useRealtimeSocket } from './realtime-provider';

interface UseCapacityRealtimeOptions {
  /** Feature-flag gate. When false the hook no-ops and opens no socket. */
  enabled: boolean;
}

/** Trailing-edge coalescing window for capacity staleness signals. */
const COALESCE_MS = 500;

/**
 * Joins `production_plan:<partnerId>` and treats every `capacity_updated`
 * event (the ENG-721 recompute persisting a factory's checks — server-side
 * throttled per partner) as a staleness ping for everything the recompute
 * moves:
 *
 *  - [THROUGHPUT_CAPACITY_KEY] — the ENG-732 capacity matrix is computed on
 *    read, so invalidating it refetches fresh cells for the summary cards and
 *    any open expanded view — the split-screen feedback loop.
 *  - the planner summary — reads the same engine.
 *  - the planner rows — ENG-847. Each row carries `at_risk`, `plan_status` and
 *    the `forecast_*` fields straight off `work_order_plan_states`, and the
 *    "Work Orders at Risk · in view" card is a client-side tally over them.
 *    Nothing else invalidates that query (the Planned Throughput save can't —
 *    the recompute is asynchronous, so a refetch on mutation-settle would just
 *    re-read the pre-recompute flags), so without this ping the card is frozen
 *    until a reload.
 *
 * Events are coalesced through a single trailing-edge timer; the 30s/3s
 * refetchInterval on the queries remains the fallback for partners without
 * realtime. Shares the ref-counted socket via `RealtimeProvider`.
 */
export function useCapacityRealtime(
  partnerId: string | undefined,
  { enabled }: UseCapacityRealtimeOptions,
): void {
  const queryClient = useQueryClient();
  const { acquireSocket, releaseSocket } = useRealtimeSocket();

  useEffect(() => {
    if (!enabled || !partnerId) return;

    const socket = acquireSocket();
    if (!socket) return;

    const chan = socket.channel(`production_plan:${partnerId}`, {});

    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      timer = null;
      void queryClient.invalidateQueries({ queryKey: [THROUGHPUT_CAPACITY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY, 'summary'] });
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY, 'partner'] });
    };

    const capacityRef = chan.on('capacity_updated', () => {
      if (timer === null) timer = setTimeout(flush, COALESCE_MS);
    });

    chan.join();

    return () => {
      chan.off('capacity_updated', capacityRef);
      if (timer !== null) clearTimeout(timer);
      chan.leave();
      releaseSocket();
    };
  }, [enabled, partnerId, acquireSocket, releaseSocket, queryClient]);
}
